import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BudgetForm, {
  BudgetFlatFormValues,
} from 'src/components/BudgetForm/BudgetForm'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { UserAssociation } from 'src/models/user.models'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface BudgetStandardContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  rcnOptions: CreditCardOption[]
  postingCreditCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  initialFlatFields?: Partial<BudgetFlatFormValues>
  setScreen: (val: BudgetScreen) => void
  postCreditCardOrBudgetRequest: (body: Partial<PostCreditCardRequest>) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  isFormDisabled: boolean
  requesting?: boolean
  approving?: boolean
}

const BudgetStandardContent: React.FC<BudgetStandardContentProps> = (props) => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const user = useSelector(selectUserListModelFromState)
  const showRequestBudget = props.requesting
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const budgetStandardActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetStandardActive,
  )

  const onSuccessfulSubmit = (flatFields: BudgetFlatFormValues): void => {
    props.postCreditCardOrBudgetRequest({
      creditCardId: showRequestBudget
        ? flatFields.parentCreditCardId
        : flatFields.creditCardId,
      approverEmail: flatFields.email,
      currency: flatFields.currency,
      emails: flatFields.emails,
      displayName: flatFields.displayName,
      issuingLimitCents:
        flatFields.issuingLimitDollars !== undefined && !flatFields.isUnlimited
          ? toCents(flatFields.issuingLimitDollars)
          : undefined,
      until:
        flatFields.untilMoment !== undefined && !flatFields.isForever
          ? flatFields.untilMoment.format('YYYY-MM-DD')
          : undefined,
      type: CreditCardType.Delegate,
      notes: flatFields.notes,
    })

    showRequestBudget
      ? analytics?.track('successfully request budget', {
          type_of_budget: 'Standard',
          budget_name: flatFields.displayName,
          budget_limit: flatFields.isUnlimited
            ? 'Unlimited'
            : '$' + flatFields.issuingLimitDollars,
          active_until: flatFields.isForever
            ? 'Never ends'
            : flatFields.untilMoment?.format('LLL'),
        })
      : analytics?.track('Successfully create budget', {
          type_of_budget: 'Standard',
          budget_name: flatFields.displayName,
          credit_limit: flatFields.isUnlimited
            ? 'Unlimited'
            : flatFields.issuingLimitDollars,
          active_until: flatFields.isForever
            ? 'Never ends'
            : flatFields.untilMoment?.format('YYYY-MM-DD'),
          is_recipient_me:
            flatFields.emails && user
              ? flatFields.emails.includes(user.email)
              : flatFields.email !== undefined
                ? flatFields.email === user?.email
                : undefined,
          recipient_count:
            flatFields.emails !== undefined
              ? flatFields.emails.length
              : flatFields.email !== undefined
                ? 1
                : 0,
          add_notes: !!flatFields.notes,
          issuer:
            creditCards &&
            flatFields.creditCardId &&
            creditCards[flatFields.creditCardId]
              ? creditCards[flatFields.creditCardId].issuer
              : undefined,
        })
  }

  return (
    <ActionRightFlyoutContent
      title={translate(
        showRequestBudget ? 'Request a Budget' : 'Create a Budget',
      )}
      specificElements={{
        subtitle: translate('Standard'),
        imageSrc: budgetStandardActiveImage,
        changeLink: {
          ariaLabelSource: 'Change Budget Type',
          changeMessage: translate('Change Budget Type'),
          onChangeClick: (): void => {
            props.setScreen(BudgetScreen.Pick)
          },
        },
      }}
      permissions={{
        isBudget: true,
      }}
    >
      <BudgetForm
        buttonText={translate(
          showRequestBudget ? 'Request a Budget' : 'Create a Budget',
        )}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingCreditCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={
          showRequestBudget ? translate('Request from') : translate('Recipient')
        }
        recurrenceEditable={false}
        rcnOptions={props.rcnOptions}
        initialFlatFields={props.initialFlatFields}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        isFormDisabled={props.isFormDisabled}
        suggestedProps={{
          showSuggested: user?.hasOrgAdminPermissions,
          showOwnUserInSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.delegateCreditCardTo]}
        unableToAddNewUser={!user?.hasOrgAdminPermissions}
        isRequestBudget={showRequestBudget}
      />
    </ActionRightFlyoutContent>
  )
}

export default BudgetStandardContent
